import { AppService } from '@ajin/services/app.service';
import { DOCUMENT, NgIf } from '@angular/common';
import {
	Component,
	Inject,
	OnDestroy,
	OnInit,
	Renderer2,
	ViewEncapsulation
} from '@angular/core';
import {
	ActivatedRoute,
	NavigationEnd,
	Router,
	RouterModule,
} from '@angular/router';
import {
	FuseNavigationItem,
	FuseNavigationService,
	FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { combineLatest, filter, map, Subject, takeUntil } from 'rxjs';

import { AjinButtonModule } from '@ajin/components/button/button.module';
import { SharedModule } from '@ajin/shared/modules/shared.module';
import { Platform } from '@angular/cdk/platform';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@ngneat/transloco';
import { Layout } from '@projects/core/layout.types';
import { NavigationService } from '../navigation/navigation.service';
import { AuthMenuModule } from './common/auth-menu/auth-menu.module';
import { MessagesModule } from './common/messages/messages.module';
import { QuickChatModule } from './common/quick-chat/quick-chat.module';
import { SearchModule } from './common/search/search.module';
import { SettingsModule } from './common/settings/settings.module';
import { ShortcutsModule } from './common/shortcuts/shortcuts.module';

@Component({
	selector: 'layout',
	templateUrl: './layout.component.html',
	styleUrls: ['./layout.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
		NgIf,
		RouterModule,
		MatButtonModule,
		MatDividerModule,
		MatIconModule,
		MatMenuModule,
		MatTooltipModule,
		MessagesModule,
		QuickChatModule,
		SearchModule,
		ShortcutsModule,
		AuthMenuModule,
		SharedModule,
		TranslocoModule,
		AjinButtonModule,
		SettingsModule,
		MatToolbarModule,
		FuseVerticalNavigationComponent,
	],
})
export class LayoutComponent implements OnInit, OnDestroy {
	config: FuseConfig;
	layout: string;
	scheme: 'dark' | 'light';
	theme: string;
	private unsubscribeAll: Subject<any> = new Subject<any>();

	navigation: FuseNavigationItem[];
	navigationAppearance: 'default' | 'dense' = 'dense';

	logoMenu: string = 'core/assets/images/logo/app.svg';
	logoMain: string = 'core/assets/images/logo/app-name.svg';
	logoMainDark: string = 'core/assets/images/logo/app-name-on-dark.svg';

	constructor(
		public app: AppService,
		public platform: Platform,
		private navigationService: NavigationService,
		private activatedRoute: ActivatedRoute,
		@Inject(DOCUMENT) private _document: any,
		private _renderer2: Renderer2,
		private _router: Router,
		private _fuseConfigService: FuseConfigService,
		private _fuseMediaWatcherService: FuseMediaWatcherService,
		private _fusePlatformService: FusePlatformService,
		private _fuseNavigationService: FuseNavigationService
	) {}

	ngOnInit(): void {
		this.navigationService.navigation$
			.pipe(takeUntil(this.unsubscribeAll))
			.subscribe((navigation: FuseNavigationItem[]) => {
				this.navigation = navigation;
			});

		this.app.screen.change
			.pipe(takeUntil(this.unsubscribeAll))
			.subscribe(() => {
				this.setNavigationAppearance();
			});

		this.setNavigationAppearance();

		combineLatest([
			this._fuseConfigService.config$,
			this._fuseMediaWatcherService.onMediaQueryChange$([
				'(prefers-color-scheme: dark)',
				'(prefers-color-scheme: light)',
			]),
		])
			.pipe(
				takeUntil(this.unsubscribeAll),
				map(([config, mql]) => {
					const options = {
						scheme: config.scheme,
						theme: config.theme,
					};

					// If the scheme is set to 'auto'...
					if (config.scheme === 'auto') {
						// Decide the scheme using the media query
						options.scheme = mql.breakpoints['(prefers-color-scheme: dark)']
							? 'dark'
							: 'light';
					}

					return options;
				})
			)
			.subscribe((options) => {
				// Store the options
				this.scheme = options.scheme;
				this.theme = options.theme;
				// Update the scheme and theme
				this._updateScheme();
				this._updateTheme();
			});

		// Subscribe to config changes
		this._fuseConfigService.config$
			.pipe(takeUntil(this.unsubscribeAll))
			.subscribe((config: FuseConfig) => {
				// Store the config
				this.config = config;

				// Update the layout
				this._updateLayout();
			});

		// Subscribe to NavigationEnd event
		this._router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.unsubscribeAll)
			)
			.subscribe(() => {
				// Update the layout
				this._updateLayout();
			});

		// Set the app version
		this._renderer2.setAttribute(
			this._document.querySelector('[ng-version]'),
			'fuse-version',
			FUSE_VERSION
		);

		// Set the OS name
		this._renderer2.addClass(
			this._document.body,
			this._fusePlatformService.osName
		);
	}

	ngOnDestroy(): void {
		this.unsubscribeAll.next(null);
		this.unsubscribeAll.complete();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Private methods
	// -----------------------------------------------------------------------------------------------------

	private _updateLayout(): void {
		// Get the current activated route
		let route = this.activatedRoute;
		while (route.firstChild) {
			route = route.firstChild;
		}

		// 1. Set the layout from the config
		this.layout = this.config.layout;

		// 2. Get the query parameter from the current route and
		// set the layout and save the layout to the config
		const layoutFromQueryParam = route.snapshot.queryParamMap.get(
			'layout'
		) as Layout;
		if (layoutFromQueryParam) {
			this.layout = layoutFromQueryParam;
			if (this.config) {
				this.config.layout = layoutFromQueryParam;
			}
		}

		const paths = route.pathFromRoot;
		paths.forEach((path) => {
			// Check if there is a 'layout' data
			if (
				path.routeConfig &&
				path.routeConfig.data &&
				path.routeConfig.data.layout
			) {
				// Set the layout
				this.layout = path.routeConfig.data.layout;
			}
		});
	}

	setNavigationAppearance(): void {
		this.navigationAppearance = !this.app.screen.md ? 'default' : 'dense';
	}

	toggleNavigation(name: string): void {
		if (this.app.screen.xs) {
			const navigation =
				this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
					name
				);

			if (navigation) {
				// Toggle the opened status
				navigation.toggle();
			}
		} else {
			this.toggleNavigationAppearance();
		}
	}

	toggleNavigationAppearance(): void {
		this.navigationAppearance =
			this.navigationAppearance === 'default' ? 'dense' : 'default';
	}

	private _updateScheme(): void {
		// Remove class names for all schemes
		this._document.body.classList.remove('light', 'dark');

		// Add class name for the currently selected scheme
		this._document.body.classList.add(this.scheme);
	}

	/**
	 * Update the selected theme
	 *
	 * @private
	 */
	private _updateTheme(): void {
		// Find the class name for the previously selected theme and remove it
		this._document.body.classList.forEach((className: string) => {
			if (className.startsWith('theme-')) {
				this._document.body.classList.remove(
					className,
					className.split('-')[1]
				);
			}
		});

		// Add class name for the currently selected theme
		this._document.body.classList.add(this.theme);
	}

	_log(event: any): void {}
}
